import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

// MUI
import {
	CheckCircle,
	NavigateBefore,
	NavigateNext,
	Person,
	Refresh,
	School,
	Send
} from "@mui/icons-material";
import {
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Container,
	Dialog,
	DialogContent,
	Divider,
	Fade,
	FormControl,
	FormControlLabel,
	FormHelperText,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Step,
	StepLabel,
	Stepper,
	TextField,
	Typography
} from "@mui/material";
import axios from "axios";
import toast from "react-hot-toast";

const API_BASE_URL = process.env.REACT_APP_API;

// Validation Schema
const validationSchema = Yup.object({
	email: Yup.string()
		.email("Invalid email address")
		.required("Email is required"),
	name: Yup.string()
		.min(2, "Name must be at least 2 characters")
		.required("Name is required"),
	father: Yup.string()
		.min(2, "Father's name must be at least 2 characters")
		.required("Father's name is required"),
	mobile: Yup.string()
		.matches(/^[0-9]{10}$/, "Mobile number must be exactly 10 digits")
		.required("Mobile number is required"),
	birth: Yup.date()
		.max(new Date(), "Birth date cannot be in the future")
		.required("Birth date is required"),
	gender: Yup.string()
		.oneOf(["Male", "Female", "Others"], "Please select a valid gender")
		.required("Gender is required"),
	address: Yup.string()
		.min(10, "Address must be at least 10 characters")
		.required("Address is required"),
	institute: Yup.string().required("Institute is required"),
	course: Yup.string().required("Course is required"),
	coupon: Yup.string(), // Optional field
	otp: Yup.string()
		.matches(/^[0-9]{6}$/, "OTP must be exactly 6 digits")
		.required("OTP is required"),
	termsAccepted: Yup.boolean()
		.oneOf([true], "You must accept the terms and conditions")
		.required("You must accept the terms and conditions"),
});

const steps = ["Contact", "Personal Info", "Academic"];

export default function AdmissionStudent() {
	const navi = useNavigate();
	const [submitted, setSubmitted] = useState(false);
	const [activeStep, setActiveStep] = useState(0);
	const [institutes, setInstitutes] = useState([]);
	const [courses, setCourses] = useState([]);
	const [selectedCourse, setSelectedCourse] = useState(null);
	const [finalPrice, setFinalPrice] = useState(null);
	const [resendingOtp, setResendingOtp] = useState(false);

	useEffect(() => {
		window.scroll(0, 0);

		// Fetch institutes and courses
		const fetchData = async () => {
			try {
				// Fetch institutes
				const institutesResponse = await axios.get(`${API_BASE_URL}/public/franchise/getall`);
				if (institutesResponse.data.status === true) {
					setInstitutes(institutesResponse.data.data || []);
				}

				// Fetch courses
				const coursesResponse = await axios.get(`${API_BASE_URL}/public/course/getall`);
				if (coursesResponse.data.status === true) {
					setCourses(coursesResponse.data.data || []);
				}
			} catch (error) {
				console.error("Error fetching data:", error);
				toast.error("Failed to load institutes and courses");
			}
		};

		fetchData();

		// Load saved step and form values from localStorage
		const savedStep = localStorage.getItem("admissionStep");
		const savedValues = localStorage.getItem("admissionFormValues");

		if (savedStep) {
			setActiveStep(parseInt(savedStep));
		}

		if (savedValues) {
			const parsedValues = JSON.parse(savedValues);
			formik.setValues(parsedValues);
		}
	}, []);

	const formik = useFormik({
		initialValues: {
			email: "",
			name: "",
			father: "",
			mobile: "",
			birth: "",
			gender: "",
			address: "",
			institute: "",
			course: "",
			coupon: "",
			otp: "",
			termsAccepted: false,
		},
		validationSchema: validationSchema,
		onSubmit: async (values) => {
			try {
				const submissionData = {
					email: values.email,
					name: values.name,
					guardian: values.father,
					mobile: values.mobile,
					birth: values.birth,
					gender: values.gender,
					address: values.address,
					institute: values.institute,
					course: values.course,
					franchise_id: values.institute,
					course_id: values.course,
					coupon: values.coupon || "",
					refer_by: localStorage.getItem('referral_code') || '',
					otp: values.otp,
				};

				const response = await axios.post(
					`${API_BASE_URL}/public/admission/join`,
					submissionData,
					{
						headers: {
							'Content-Type': 'application/json',
						},
					}
				);

				if (response.data.status === true) {
					toast.success(response.data.message || "Admission successful!");
					// Clear localStorage on successful submission
					localStorage.removeItem("admissionStep");
					localStorage.removeItem("admissionFormValues");
					setSubmitted(true);
				} else {
					toast.error(response.data.message || "Admission failed");
				}
			} catch (error) {
				toast.error(error.response?.data?.message || "Failed to submit admission. Please try again.");
			}
		},
	});

	// Save form values to localStorage whenever they change
	useEffect(() => {
		localStorage.setItem("admissionFormValues", JSON.stringify(formik.values));
	}, [formik.values]);

	// Save active step to localStorage whenever it changes
	useEffect(() => {
		localStorage.setItem("admissionStep", activeStep.toString());
	}, [activeStep]);

	const [validatingEmail, setValidatingEmail] = useState(false);

	// Handle course selection
	const handleCourseChange = (event) => {
		const courseId = event.target.value;
		formik.handleChange(event);

		const course = courses.find(c => c.courseid === courseId);
		setSelectedCourse(course);
		setFinalPrice(course ? course.c_price : null);

		// Reset coupon if course changes
		formik.setFieldValue('coupon', '');
	};

	// Handle coupon validation
	useEffect(() => {
		if (selectedCourse && formik.values.coupon) {
			// Check if coupon matches
			if (formik.values.coupon === selectedCourse.c_coupon) {
				// Apply discount
				const discount = selectedCourse.c_coupon_amount || 0;
				const discountedPrice = selectedCourse.c_price - discount;
				setFinalPrice(discountedPrice > 0 ? discountedPrice : 0);
				toast.success(`Coupon applied! You saved ₹${discount}`);
			} else {
				// Invalid coupon - reset to original price
				setFinalPrice(selectedCourse.c_price);
			}
		} else if (selectedCourse) {
			// No coupon - use original price
			setFinalPrice(selectedCourse.c_price);
		}
	}, [formik.values.coupon, selectedCourse]);

	// Resend OTP function
	const resendOtp = async () => {
		setResendingOtp(true);
		try {
			const otpFormData = new FormData();
			otpFormData.append("email", formik.values.email);

			const response = await axios.post(`${API_BASE_URL}/class/send-otp`, otpFormData);

			if (response.data.status === true) {
				toast.success(response.data.message || "OTP sent successfully!");
			} else {
				toast.error(response.data.message || "Failed to send OTP");
			}
		} catch (error) {
			toast.error(error.response?.data?.message || "Failed to send OTP. Please try again.");
		} finally {
			setResendingOtp(false);
		}
	};

	const handleNext = async () => {
		// Validate current step before moving forward
		let fieldsToValidate = [];

		switch (activeStep) {
			case 0:
				fieldsToValidate = ["email"];
				break;
			case 1:
				fieldsToValidate = ["name", "father", "mobile", "birth", "gender", "address"];
				break;
			case 2:
				fieldsToValidate = ["institute", "course", "otp", "termsAccepted"];
				break;
			default:
				break;
		}

		// Touch fields to show errors
		fieldsToValidate.forEach(field => formik.setFieldTouched(field, true));

		// Check if current step is valid
		const hasErrors = fieldsToValidate.some(field => formik.errors[field]);

		if (!hasErrors && activeStep < steps.length - 1) {
			// If on step 0 (email step), validate email via API
			if (activeStep === 0) {
				setValidatingEmail(true);
				try {
					const formData = new FormData();
					formData.append("email", formik.values.email);

					const response = await axios.post(`${API_BASE_URL}/public/admission/verify`, formData);

					if (response.data.status === true) {
						toast.success(response.data.message || "Email verified successfully!");

						// Send OTP after successful email verification
						try {
							const otpFormData = new FormData();
							otpFormData.append("email", formik.values.email);

							const otpResponse = await axios.post(`${API_BASE_URL}/class/send-otp`, otpFormData);

							if (otpResponse.data.status === true) {
								toast.success(otpResponse.data.message || "OTP sent successfully!");
								setActiveStep((prevActiveStep) => prevActiveStep + 1);
							} else {
								toast.error(otpResponse.data.message || "Failed to send OTP");
							}
						} catch (otpError) {
							toast.error(otpError.response?.data?.message || "Failed to send OTP. Please try again.");
						}
					} else {
						toast.error(response.data.message || "Email verification failed");
					}
				} catch (error) {
					toast.error(error.response?.data?.message || "Failed to verify email. Please try again.");
				} finally {
					setValidatingEmail(false);
				}
			} else {
				// For other steps, just move to next step
				setActiveStep((prevActiveStep) => prevActiveStep + 1);
			}
		}
	};

	const handleBack = () => {
		setActiveStep((prevActiveStep) => prevActiveStep - 1);
	};

	const handleReset = () => {
		formik.resetForm();
		setActiveStep(0);
		setSelectedCourse(null);
		setFinalPrice(null);
		localStorage.removeItem("admissionStep");
		localStorage.removeItem("admissionFormValues");
		toast.success("Form has been reset");
	};

	const renderStepContent = (step) => {
		switch (step) {
			case 0:
				return (
					<Fade in={true} timeout={500}>
						<Box>
							<Box textAlign="center" mb={4}>
								<Box
									sx={{
										width: 80,
										height: 80,
										borderRadius: "50%",
										bgcolor: "#E3F2FD",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										margin: "0 auto",
										mb: 2,
									}}
								>
									<Person sx={{ fontSize: 40, color: "#1976d2" }} />
								</Box>
								<Typography variant="h5" fontWeight={600} mb={1}>
									Let's Get Started
								</Typography>
								<Typography color="text.secondary" fontSize={14}>
									Enter your email to begin the admission process
								</Typography>
							</Box>
							<TextField
								fullWidth
								variant="outlined"
								type="email"
								label="Email Address"
								name="email"
								value={formik.values.email}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								error={formik.touched.email && Boolean(formik.errors.email)}
								helperText={formik.touched.email && formik.errors.email}
								sx={{ mb: 2 }}
							/>
						</Box>
					</Fade>
				);

			case 1:
				return (
					<Fade in={true} timeout={500}>
						<Box>
							<Box textAlign="center" mb={4}>
								<Box
									sx={{
										width: 80,
										height: 80,
										borderRadius: "50%",
										bgcolor: "#F3E5F5",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										margin: "0 auto",
										mb: 2,
									}}
								>
									<Person sx={{ fontSize: 40, color: "#9c27b0" }} />
								</Box>
								<Typography variant="h5" fontWeight={600} mb={1}>
									Personal Information
								</Typography>
								<Typography color="text.secondary" fontSize={14}>
									Tell us about yourself
								</Typography>
							</Box>
							<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
								<TextField
									fullWidth
									variant="outlined"
									type="text"
									label="Full Name"
									name="name"
									value={formik.values.name}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									error={formik.touched.name && Boolean(formik.errors.name)}
									helperText={formik.touched.name && formik.errors.name}
								/>
								<TextField
									fullWidth
									variant="outlined"
									type="text"
									label="Father's Name"
									name="father"
									value={formik.values.father}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									error={formik.touched.father && Boolean(formik.errors.father)}
									helperText={formik.touched.father && formik.errors.father}
								/>
								<TextField
									fullWidth
									variant="outlined"
									type="tel"
									label="Mobile Number"
									name="mobile"
									value={formik.values.mobile}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									error={formik.touched.mobile && Boolean(formik.errors.mobile)}
									helperText={formik.touched.mobile && formik.errors.mobile}
									inputProps={{ maxLength: 10 }}
								/>
								<TextField
									fullWidth
									variant="outlined"
									type="date"
									label="Date of Birth"
									name="birth"
									value={formik.values.birth}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									error={formik.touched.birth && Boolean(formik.errors.birth)}
									helperText={formik.touched.birth && formik.errors.birth}
									InputLabelProps={{ shrink: true }}
								/>
								<FormControl
									fullWidth
									error={formik.touched.gender && Boolean(formik.errors.gender)}
								>
									<InputLabel>Gender</InputLabel>
									<Select
										label="Gender"
										name="gender"
										value={formik.values.gender}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
									>
										<MenuItem value="Male">Male</MenuItem>
										<MenuItem value="Female">Female</MenuItem>
										<MenuItem value="Others">Others</MenuItem>
									</Select>
									{formik.touched.gender && formik.errors.gender && (
										<FormHelperText>{formik.errors.gender}</FormHelperText>
									)}
								</FormControl>
								<TextField
									fullWidth
									variant="outlined"
									type="text"
									label="Complete Address"
									name="address"
									multiline
									rows={3}
									value={formik.values.address}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									error={formik.touched.address && Boolean(formik.errors.address)}
									helperText={formik.touched.address && formik.errors.address}
								/>
							</Box>
						</Box>
					</Fade>
				);

			case 2:
				return (
					<Fade in={true} timeout={500}>
						<Box>
							<Box textAlign="center" mb={4}>
								<Box
									sx={{
										width: 80,
										height: 80,
										borderRadius: "50%",
										bgcolor: "#FFF3E0",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										margin: "0 auto",
										mb: 2,
									}}
								>
									<School sx={{ fontSize: 40, color: "#f57c00" }} />
								</Box>
								<Typography variant="h5" fontWeight={600} mb={1}>
									Academic Preferences
								</Typography>
								<Typography color="text.secondary" fontSize={14}>
									Choose your institute and course
								</Typography>
							</Box>
							<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
								<FormControl
									fullWidth
									error={formik.touched.institute && Boolean(formik.errors.institute)}
								>
									<InputLabel>Select Institute</InputLabel>
									<Select
										label="Select Institute"
										name="institute"
										value={formik.values.institute}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
									>
										{institutes.map((inst) => (
											<MenuItem value={inst.franchiseid} key={inst.franchiseid}>
												<Box>
													<Typography fontWeight={500}>
														{inst.fr_name}
													</Typography>
													<Typography fontSize={12} color="text.secondary">
														Code: {inst.fr_code}
													</Typography>
												</Box>
											</MenuItem>
										))}
									</Select>
									{formik.touched.institute && formik.errors.institute && (
										<FormHelperText>{formik.errors.institute}</FormHelperText>
									)}
								</FormControl>

								<FormControl
									fullWidth
									error={formik.touched.course && Boolean(formik.errors.course)}
								>
									<InputLabel>Select Course</InputLabel>
									<Select
										label="Select Course"
										name="course"
										value={formik.values.course}
										onChange={handleCourseChange}
										onBlur={formik.handleBlur}
									>
										{courses.map((course) => (
											<MenuItem value={course.courseid} key={course.courseid}>
												<Box>
													<Typography fontWeight={500}>
														{course.c_title}
													</Typography>
													<Typography fontSize={12} color="text.secondary">
														{course.c_name} • {course.c_duration} Months
													</Typography>
												</Box>
											</MenuItem>
										))}
									</Select>
									{formik.touched.course && formik.errors.course && (
										<FormHelperText>{formik.errors.course}</FormHelperText>
									)}
								</FormControl>

								<TextField
									fullWidth
									variant="outlined"
									type="text"
									label="Coupon Code (Optional)"
									name="coupon"
									value={formik.values.coupon}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									error={formik.touched.coupon && Boolean(formik.errors.coupon)}
									helperText={formik.touched.coupon && formik.errors.coupon}
								/>

								{/* Course Details Display */}
								{selectedCourse && (
									<Box
										sx={{
											p: 2,
											bgcolor: "#f5f5f5",
											borderRadius: 2,
											border: "1px solid #e0e0e0",
										}}
									>
										<Typography variant="h6" fontWeight={600} mb={1}>
											Course Details
										</Typography>
										<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
											<Box sx={{ display: "flex", justifyContent: "space-between" }}>
												<Typography fontSize={14} color="text.secondary">
													Course:
												</Typography>
												<Typography fontSize={14} fontWeight={500}>
													{selectedCourse.c_title} - {selectedCourse.c_name}
												</Typography>
											</Box>
											<Box sx={{ display: "flex", justifyContent: "space-between" }}>
												<Typography fontSize={14} color="text.secondary">
													Duration:
												</Typography>
												<Typography fontSize={14} fontWeight={500}>
													{selectedCourse.c_duration} Months
												</Typography>
											</Box>
											<Box sx={{ display: "flex", justifyContent: "space-between" }}>
												<Typography fontSize={14} color="text.secondary">
													Original Price:
												</Typography>
												<Typography
													fontSize={14}
													fontWeight={500}
													sx={{
														textDecoration: finalPrice < selectedCourse.c_price ? "line-through" : "none",
														color: finalPrice < selectedCourse.c_price ? "text.secondary" : "text.primary",
													}}
												>
													₹{selectedCourse.c_max_price}
												</Typography>
											</Box>
											{finalPrice < selectedCourse.c_price && (
												<Box sx={{ display: "flex", justifyContent: "space-between" }}>
													<Typography fontSize={14} color="success.main" fontWeight={600}>
														Discounted Price:
													</Typography>
													<Typography fontSize={14} color="success.main" fontWeight={600}>
														₹{finalPrice}
													</Typography>
												</Box>
											)}
											<Box sx={{ display: "flex", justifyContent: "space-between" }}>
												<Typography fontSize={14} fontWeight={600}>
													Final Price:
												</Typography>
												<Typography fontSize={16} fontWeight={700} color="primary">
													₹{finalPrice}
												</Typography>
											</Box>
											{finalPrice < selectedCourse.c_price && (
												<Typography fontSize={12} color="success.main" textAlign="center" mt={1}>
													You save ₹{selectedCourse.c_price - finalPrice}!
												</Typography>
											)}
										</Box>
									</Box>
								)}

								{/* OTP Input with Resend Button */}
								<Box>
									<Typography fontSize={14} fontWeight={500} mb={1}>
										Enter OTP sent to your email
									</Typography>
									<Box sx={{ display: "flex", gap: 1 }}>
										<TextField
											fullWidth
											variant="outlined"
											type="text"
											label="Enter OTP"
											name="otp"
											value={formik.values.otp}
											onChange={formik.handleChange}
											onBlur={formik.handleBlur}
											error={formik.touched.otp && Boolean(formik.errors.otp)}
											helperText={formik.touched.otp && formik.errors.otp}
											inputProps={{ maxLength: 6 }}
										/>
										<Button
											variant="outlined"
											onClick={resendOtp}
											disabled={resendingOtp}
											sx={{ minWidth: 120, height: 56 }}
										>
											{resendingOtp ? <CircularProgress size={24} /> : "Resend OTP"}
										</Button>
									</Box>
								</Box>

								<Divider sx={{ my: 1 }} />

								<FormControl
									error={formik.touched.termsAccepted && Boolean(formik.errors.termsAccepted)}
								>
									<FormControlLabel
										control={
											<Checkbox
												color="primary"
												name="termsAccepted"
												checked={formik.values.termsAccepted}
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
											/>
										}
										label={
											<Typography fontSize={14}>
												I agree to the{" "}
												<Box
													component="span"
													sx={{
														color: "primary.main",
														cursor: "pointer",
														textDecoration: "underline",
													}}
													onClick={() => navi("/terms&conditions")}
												>
													terms and conditions
												</Box>
											</Typography>
										}
									/>
									{formik.touched.termsAccepted && formik.errors.termsAccepted && (
										<FormHelperText>{formik.errors.termsAccepted}</FormHelperText>
									)}
								</FormControl>
							</Box>
						</Box>
					</Fade>
				);

			default:
				return null;
		}
	};

	return (
		<Container maxWidth="md">
			{/* Success Dialog */}
			<Dialog open={submitted} fullWidth maxWidth="sm">
				<DialogContent>
					<Box textAlign="center" py={4}>
						<CheckCircle sx={{ fontSize: 120, color: "#4CAF50", mb: 3 }} />
						<Typography variant="h4" fontWeight={700} color="#4CAF50" gutterBottom>
							Application Submitted!
						</Typography>
						<Typography color="text.secondary" fontSize={16} mb={1}>
							Thank you for submitting your admission application.
						</Typography>
						<Typography color="text.secondary" fontSize={14} mb={4}>
							We will review your details and contact you soon. Welcome to the SpyWare family!
						</Typography>
						<Button
							variant="contained"
							color="success"
							size="large"
							onClick={() => navi("/")}
							sx={{ minWidth: 200 }}
						>
							Go to Home
						</Button>
					</Box>
				</DialogContent>
			</Dialog>

			{/* Main Form */}
			<Box component="form" onSubmit={formik.handleSubmit} sx={{ py: 4 }}>
				{/* Header */}
				<Box textAlign="center" mb={5}>
					<Chip
						label="Admission Form"
						color="primary"
						sx={{ mb: 2, fontSize: 12, fontWeight: 600 }}
					/>
					<Typography variant="h3" fontWeight={700} mb={1}>
						Join SpyWare
					</Typography>
					<Typography color="text.secondary" fontSize={16}>
						Complete the form below to start your learning journey
					</Typography>
				</Box>

				{/* Stepper */}
				<Paper
					elevation={0}
					sx={{
						p: { xs: 2, md: 4 },
						border: "2px solid #f5f5f5",
						borderRadius: 3
					}}
				>
					<Stepper
						activeStep={activeStep}
						alternativeLabel
						sx={{
							mb: 4,
							'& .MuiStepLabel-label': {
								fontSize: { xs: '0.75rem', sm: '0.875rem' },
								mt: { xs: 0.5, sm: 1 }
							},
							'& .MuiStepConnector-line': {
								display: { xs: 'none', sm: 'block' }
							}
						}}
					>
						{steps.map((label) => (
							<Step key={label}>
								<StepLabel>{label}</StepLabel>
							</Step>
						))}
					</Stepper>

					{/* Step Content */}
					<Box sx={{ minHeight: 400, mb: 4 }}>
						{renderStepContent(activeStep)}
					</Box>

					{/* Navigation Buttons */}
					<Box sx={{ display: "flex", justifyContent: "space-between", pt: 2 }}>
						<Button
							disabled={activeStep === 0}
							onClick={handleBack}
							startIcon={<NavigateBefore />}
							sx={{ minWidth: 120 }}
						>
							Back
						</Button>

						<Box sx={{ display: "flex", gap: 1 }}>
							{activeStep === steps.length - 1 && (
								<Button
									variant="outlined"
									color="error"
									onClick={handleReset}
									startIcon={<Refresh />}
									sx={{ minWidth: 120 }}
								>
									Reset Form
								</Button>
							)}
							{activeStep === steps.length - 1 ? (
								<Button
									variant="contained"
									onClick={formik.handleSubmit}
									endIcon={<Send />}
									sx={{ minWidth: 120 }}
								>
									Submit
								</Button>
							) : (
								<Button
									variant="contained"
									onClick={handleNext}
									disabled={validatingEmail}
									endIcon={validatingEmail ? null : <NavigateNext />}
									sx={{ minWidth: 120 }}
								>
									{validatingEmail ? <CircularProgress size={24} color="inherit" /> : "Next"}
								</Button>
							)}
						</Box>
					</Box>
				</Paper>
			</Box>

			{/* Progress Indicator */}
			<Box textAlign="center" mt={3}>
				<Typography fontSize={12} color="text.secondary">
					Step {activeStep + 1} of {steps.length}
				</Typography>
			</Box>
		</Container >
	);
}
